import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { setReturnUrl, isSafeReturnUrl } from './utils/return-url';
import { getLoginMode } from './utils/login-mode';

export const authGuard: CanActivateFn = async (route, state) => {
  const msalService = inject(MsalService);
  const msalBroadcastService = inject(MsalBroadcastService);
  const router = inject(Router);

  // 1. If MSAL is processing a B2C login redirect (hash in URL), let it through.
  //    AppComponent.handleRedirectObservable() owns this flow.
  const hasMsalHash = window.location.hash.includes('state=') ||
                      window.location.hash.includes('code=') ||
                      window.location.hash.includes('id_token=');
  if (hasMsalHash) {
    // Super Admin (CIAM) redirects must never fall into the tenant shell —
    // there's no RFP session/token for this identity, so IndexComponent's
    // dashboard calls would all 401. Send it straight to the placeholder page.
    if (getLoginMode() === 'super-admin') {
      router.navigate(['/super-admin'], { replaceUrl: true });
      return false;
    }
    return true;
  }

  // 2. Wait for MSAL to reach a stable "None" state.
  //    DO NOT use a timeout here. The firstValueFrom will resolve the moment
  //    MSAL finishes its internal redirect-handling process (ms to 1-2s).
  //    A timeout causes a false-negative: accounts appear empty while MSAL
  //    is still loading from cache, triggering the /login flicker loop.
  await firstValueFrom(
    msalBroadcastService.inProgress$.pipe(
      filter((status: InteractionStatus) => status === InteractionStatus.None)
    )
  );

  // 3. MSAL is now fully settled. Check accounts with confidence.
  //    A Super Admin (CIAM) account is still visible here — MSAL's sessionStorage
  //    cache is shared across PublicClientApplication instances — so it must be
  //    excluded explicitly, same as the hash case above.
  if (getLoginMode() === 'super-admin') {
    router.navigate(['/super-admin'], { replaceUrl: true });
    return false;
  }

  const accounts = msalService.instance.getAllAccounts();
  if (accounts.length > 0) {
    if (!msalService.instance.getActiveAccount()) {
      msalService.instance.setActiveAccount(accounts[0]);
    }
    return true;
  }

  // 4. MSAL is settled AND no accounts exist → truly unauthenticated.
  //    Preserve a shared deep link (e.g. /rfp/123/analyze) so we can return the
  //    user there after login, and send them straight to the sign-in page.
  //    A bare-root visit has nothing worth preserving → show the marketing page.
  if (isSafeReturnUrl(state.url)) {
    setReturnUrl(state.url);
    router.navigate(['/login'], { queryParams: { returnUrl: state.url }, replaceUrl: true });
  } else {
    router.navigate(['/welcome'], { replaceUrl: true });
  }
  return false;
};

